import { OrcamentoIcon } from './OrcamentoUI';
import { useLiquidFeedback, LiquidDialog } from './LiquidUI';
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './CardManagerModal.css';

const CardManagerModal = ({ onClose, onCartoesChange }) => {
    const liquidFeedback = useLiquidFeedback();
    const [cartoes, setCartoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [nome, setNome] = useState('');
    const [numero, setNumero] = useState('');
    const [empresa, setEmpresa] = useState('Sprod'); // os movimentos do banco herdam-na daqui
    const [pedeEf, setPedeEf] = useState(false); // EF e Projeto no detalhe das despesas
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchCartoes();
    }, []);

    const fetchCartoes = async () => {
        try {
            const res = await api.get('/despesas/cartoes');
            setCartoes(res.data);
            if (onCartoesChange) onCartoesChange();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e, forcar = false) => {
        if (e) e.preventDefault();
        setSubmitting(true);
        try {
            const dados = { nome, numero_mascarado: numero, empresa, pede_ef: pedeEf, force: forcar };
            if (editingId) {
                await api.put(`/despesas/cartoes/${editingId}`, dados);
            } else {
                await api.post('/despesas/cartoes', dados);
            }
            setNome('');
            setNumero('');
            setEmpresa('Sprod');
            setPedeEf(false);
            setEditingId(null);
            fetchCartoes();
        } catch (err) {
            const d = err.response?.data;
            /* Últimos quatro dígitos repetidos. Não é proibido — dois cartões
               reais podem terminar igual — mas tem de ser uma decisão tomada,
               não um descuido: o reconciliador de extratos identifica os
               cartões por estes quatro dígitos e passaria a tratar os dois
               como um só, sem dar erro nenhum. */
            if (err.response?.status === 409 && d?.colisao_digitos) {
                const seguir = await liquidFeedback.confirm(
                    `${d.error}

`
                    + `Se forem mesmo dois cartões diferentes, os movimentos do extrato `
                    + `de um podem ser atribuídos ao outro, sem aviso.

`
                    + `Gravar mesmo assim?`);
                setSubmitting(false);
                if (seguir) await handleSubmit(null, true);
                return;
            }
            console.error(err);
            const status = err.response?.status || 'N/A';
            const msg = d?.error || err.message || 'Erro desconhecido';
            liquidFeedback.notify(`Erro (${status}): ${msg}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!await liquidFeedback.confirm('Tem a certeza que deseja inativar este cartão?')) return;
        try {
            await api.delete(`/despesas/cartoes/${id}`);
            fetchCartoes();
        } catch (err) {
            console.error(err);
            liquidFeedback.notify('Erro ao inativar cartão.');
        }
    };

    const startEdit = (card) => {
        setEditingId(card.id);
        setNome(card.nome);
        setNumero(card.numero_mascarado);
        setEmpresa(card.empresa || 'Sprod');
        setPedeEf(!!card.pede_ef);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setNome('');
        setNumero('');
        setEmpresa('Sprod');
        setPedeEf(false);
    };

    return (
        <LiquidDialog onClose={onClose} className="card-manager-overlay" onClick={onClose}>
            <div className="card-manager-modal" onClick={e => e.stopPropagation()}>
                <div className="card-manager-header">
                    <h2><OrcamentoIcon name="card" fallback="💳" /> Gerir Cartões</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <form className="card-form" onSubmit={handleSubmit}>
                    <h4>{editingId ? 'Editar Cartão' : 'Novo Cartão'}</h4>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Nome do Cartão</label>
                            <input
                                type="text"
                                value={nome}
                                onChange={e => setNome(e.target.value)}
                                placeholder="Ex: Débito BPI"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Número (Fim 4 dig.)</label>
                            <input
                                type="text"
                                value={numero}
                                onChange={e => setNumero(e.target.value)}
                                placeholder="**** 1234"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Empresa</label>
                            <select value={empresa} onChange={e => setEmpresa(e.target.value)}>
                                <option value="Sprod">Sprod</option>
                                <option value="Besmart">Besmart</option>
                            </select>
                        </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 12px', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={pedeEf} onChange={e => setPedeEf(e.target.checked)} />
                        Pedir EF e Projeto no detalhe das despesas (ex.: cartão das compras online)
                    </label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button type="submit" className="btn-save" disabled={submitting}>
                            {editingId ? 'Atualizar' : 'Adicionar Cartão'}
                        </button>
                        {editingId && (
                            <button type="button" className="btn-cancel" onClick={cancelEdit} disabled={submitting}>
                                Cancelar
                            </button>
                        )}
                    </div>
                </form>

                <div className="cards-list">
                    {loading ? <p>A carregar...</p> : cartoes.map(card => (
                        <div key={card.id} className="card-item">
                            <div className="card-info">
                                <h5>{card.nome}</h5>
                                <p>{card.numero_mascarado} · {card.empresa || 'Sprod'}{card.pede_ef ? ' · pede EF e Projeto' : ''}</p>
                            </div>
                            <div className="card-actions">
                                <button className="icon-btn edit" onClick={() => startEdit(card)} title="Editar"><OrcamentoIcon name="edit" fallback="✏️" /></button>
                                <button className="icon-btn delete" onClick={() => handleDelete(card.id)} title="Inativar"><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                            </div>
                        </div>
                    ))}
                    {!loading && cartoes.length === 0 && (
                        <p style={{ textAlign: 'center', color: '#718096' }}>Nenhum cartão registado.</p>
                    )}
                </div>
            </div>
        </LiquidDialog>
    );
};

export default CardManagerModal;
